import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { secureGet, secureDelete, securePut } from '../../config/api';
import { PrimaryButton } from '../../components/PrimaryButton';
import Breadcrumbs from '../../components/Breadcrumbs';
import SnackbarNotification from "../../components/SnackbarNotification";
import ModuleWrapper from "../../components/ModuleWrapper";
import UserContext from "../../Shared/UserContext";
import DetailsHeader from '../../components/DetailsHeader';
import Filter from '../../components/Filter';
import GenericCard from '../Users/GenericCard';
import OptionsMenu from '../../components/OptionsMenu';
import DeletePopup from '../../components/DeletePopup';
import ProductRow from '../../components/ProductRow';
import LoadingLogo from '../../components/LoadingLogo';
import { formatAmount } from '../../utils/format';

import PrintIcon from '../../icons/printer.svg';
import ListIcon from '../../icons/list.svg';
import FileIcon from '../../icons/file-text1.svg';
import EditIcon from '../../icons/editB.svg';
import AddIcon from '../../icons/plusB.svg';
import plusIcon from '../../icons/plus.svg';
import EyeIcon from '../../icons/eye.svg';
import DownloadIcon from '../../icons/downloadB.svg';
import GenerateIcon from '../../icons/generateprj.svg';
import TrashIcon from '../../icons/trashW.svg';
import ChevronDownIcon from '../../icons/chevron-down.svg';
import DeleteIcon from '../../icons/trashR.svg';
import HistoryIcon from '../../icons/historyIcon.svg';

const FicheReceptionDetails = () => {
  // ========== State Declarations ==========
  const { state: { role } } = useContext(UserContext);
  const { t } = useTranslation();
  const { id } = useParams();

  const [activeTab, setActiveTab] = useState('quotes');
  const [loading, setLoading] = useState(false);
  const [showModule, setShowModule] = useState(false);
  const [showDocumentModule, setShowDocumentModule] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [, setSelectedDocument] = useState(null);

  const [fr, setfr] = useState({});
  const [vehicle, setVehicle] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteNotFound, setQuoteNotFound] = useState(false);
  const [creditNotes, setCreditNotes] = useState([]);
  const [documents, setDocuments] = useState([]);

  const [selectedVersionIdx, setSelectedVersionIdx] = useState({});
  const [revisionIds, setRevisionIds] = useState([]);
  const [revisionDetails, setRevisionDetails] = useState({});
  const [historyDropdownOpen, setHistoryDropdownOpen] = useState({});
  const dropdownRefs = useRef({});

  const [deletePopupOpen, setDeletePopupOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [deleteDocumentPopupOpen, setDeleteDocumentPopupOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [signing, setSigning] = useState(false);

  const [documentFilters, setDocumentFilters] = useState({ type: "" });
  const [loadedTabs, setLoadedTabs] = useState({
    quotes: false,
    bri: false,
    documents: false,
  });
  const [tabLoading, setTabLoading] = useState({
    quotes: false,
    bri: false,
    documents: false,
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  // ========== Helper Functions ==========
  const getSelectedVersion = (q) => {
    const versionIdx = selectedVersionIdx[q.id];
    if (versionIdx !== undefined) {
      return versionIdx + 1;
    }
    return q.revisionNumber || 1;
  };

  const setVersion = (qId, versionNumber) =>
    setSelectedVersionIdx((s) => ({ ...s, [qId]: versionNumber - 1 }));

  const ensureDefaults = useCallback((entities = []) => {
    if (!entities.length) {
      return;
    }

    setSelectedVersionIdx((prev) => {
      const next = { ...prev };
      let mutated = false;

      entities.forEach((entity) => {
        if (!entity?.id) {
          return;
        }

        if (next[entity.id] === undefined) {
          const latestVersion = entity.revisionNumber || 1;
          next[entity.id] = latestVersion - 1;
          mutated = true;
        }
      });

      return mutated ? next : prev;
    });
  }, []);

  // ========== Fetch Functions ==========
  const fetchVehicle = async (vehicleId) => {
    if (!vehicleId) return;

    const abortController = new AbortController();
    try {
      const response = await secureGet(`/vehicles/${vehicleId}`, showSnackbar, {
        signal: abortController.signal
      });
      setVehicle(response.data);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch vehicle:", error);
      }
    }
  };

  const fetchfr = useCallback(async () => {
    const abortController = new AbortController();
    setLoading(true);
    try {
      const response = await secureGet(`/reception-documents/${id}`, showSnackbar, {
        signal: abortController.signal
      });
      setfr(response.data);
      console.log("fr fetched data:", response.data);

      if (response.data?.vehicle?.id) {
        await fetchVehicle(response.data?.vehicle?.id);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch fr:", error);
      }
    } finally {
      setLoading(false);
    }
    return () => {
      abortController.abort();
    };
  }, [id]);

  const fetchQuoteSummary = useCallback(async () => {
    if (!id) return;

    const abortController = new AbortController();
    try {
      const response = await secureGet(`/quotes/reception-document/${id}/summary`, showSnackbar, {
        signal: abortController.signal
      });
      console.log("[FETCH SUMMARY] ✅ Successfully fetched summary:", response.data);

      const summaryData = response.data || {};
      const revisionIdsArray = summaryData.revisionIds || [];
      setRevisionIds(revisionIdsArray);

      console.log(`[FETCH SUMMARY] Found ${revisionIdsArray.length} revision IDs:`, revisionIdsArray);

      if (quote) {
        ensureDefaults([{ id: summaryData.quoteId || quote.id, revisionNumber: revisionIdsArray.length + 1 }]);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("[FETCH SUMMARY] ❌ Failed to fetch summary:", error);
      }
    }

    return () => {
      abortController.abort();
    };
  }, [id, quote, ensureDefaults]);

  const fetchQuote = useCallback(async () => {
    if (!id || loadedTabs.quotes) return;

    const abortController = new AbortController();
    setTabLoading(prev => ({ ...prev, quotes: true }));
    setQuoteNotFound(false);
    try {
      const response = await secureGet(`/quotes/reception-document/${id}`, null, {
        signal: abortController.signal
      });
      console.log("[FETCH QUOTE] ✅ Successfully fetched quote:", response.data);

      const quoteData = response.data || null;
      setQuote(quoteData);
      setQuoteNotFound(false);
      setLoadedTabs(prev => ({ ...prev, quotes: true }));
      ensureDefaults(quoteData ? [quoteData] : []);

      if (quoteData && (role === "ROLE_SERVICE_CONSULTANT" || role === "ROLE_ADMIN" || role === "ROLE_WORKSHOP_MANAGER")) {
        await fetchQuoteSummary();
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        const statusCode = error.response?.status || error.status;

        if (statusCode === 404) {
          setQuoteNotFound(true);
          setQuote(null);
          setLoadedTabs(prev => ({ ...prev, quotes: true }));
        } else {
          showSnackbar(error.response?.data?.statusLabel || i18n.t('errors.generic'), 'error', true);
        }
      }
    } finally {
      setTabLoading(prev => ({ ...prev, quotes: false }));
    }

    return () => {
      abortController.abort();
    };
  }, [id, loadedTabs.quotes, ensureDefaults, fetchQuoteSummary, role]);

  const fetchCreditNotes = useCallback(async () => {
    if (!id || loadedTabs.bri) return;

    const abortController = new AbortController();
    setTabLoading(prev => ({ ...prev, bri: true }));
    try {
      const response = await secureGet(`/creditNotes/reception-document/${id}`, showSnackbar, {
        signal: abortController.signal
      });

      setCreditNotes(response.data.content || []);
      setLoadedTabs(prev => ({ ...prev, bri: true }));

      console.log("credit notes fetched data:", response.data);
      ensureDefaults(response.data || []);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch credit notes:", error);
      }
    } finally {
      setTabLoading(prev => ({ ...prev, bri: false }));
    }

    return () => {
      abortController.abort();
    };
  }, [id, loadedTabs.bri, ensureDefaults]);

  const fetchDocuments = useCallback(async () => {
    if (!id || loadedTabs.documents) return;

    const abortController = new AbortController();
    setTabLoading(prev => ({ ...prev, documents: true }));
    try {
      const params = new URLSearchParams();
      if (documentFilters.type) params.append("type", documentFilters.type);
      const url = `/reception-documents/${id}/attachments${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await secureGet(url, showSnackbar, {
        signal: abortController.signal
      });

      setDocuments(response.data || []);
      setLoadedTabs(prev => ({ ...prev, documents: true }));

      console.log("documents fetched data:", response.data);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch documents:", error);
      }
    } finally {
      setTabLoading(prev => ({ ...prev, documents: false }));
    }

    return () => {
      abortController.abort();
    };
  }, [id, loadedTabs.documents, documentFilters.type]);

  // ========== Event Handlers - Reception Document ==========
  const handleMarkAsSigned = async () => {
    setSigning(true);
    try {
      await securePut(`/reception-documents/${fr.id}/sign`, {}, showSnackbar);
      showSnackbar(t('confirmation.ficheReceptionSigned'), 'success');
      fetchfr();
    } catch (error) {
      console.error("Failed to mark as signed:", error);
    } finally {
      setSigning(false);
    }
  };

  // ========== Event Handlers - Quotes ==========
  const handleAddClick = () => {
    setSelectedQuote(null);
    setShowModule(true);
  };

  const handleEditClick = (quote) => {
    setSelectedQuote(quote);
    setShowModule(true);
  };

  const handleDeleteClick = (quote) => {
    setQuoteToDelete(quote);
    setDeletePopupOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!quoteToDelete) return;
    try {
      await secureDelete(`/quotes/delete/${quoteToDelete.id}`, showSnackbar);
      showSnackbar(t('.quoteDelete'), 'success');
      setDeletePopupOpen(false);
      setQuoteToDelete(null);
      setLoadedTabs(prev => ({ ...prev, quote: false }));
      fetchfr();
      fetchQuote();
      setQuote(null);
    } catch (error) {
      console.error('Error deleting quote:', error);
    }
  };

  const handleHistoryToggle = (quoteId) => {
    setHistoryDropdownOpen(prev => ({
      ...prev,
      [quoteId]: !prev[quoteId]
    }));
  };

  const handleVersionSelect = async (quoteId, revisionValue) => {
    const versionNumber = revisionValue + 1;

    setVersion(quoteId, versionNumber);
    setHistoryDropdownOpen((prev) => ({ ...prev, [quoteId]: false }));

    const quoteNumber = quote?.quoteNumber;
    if (!quoteNumber) {
      return;
    }

    let revisionId = null;
    let apiEndpoint = '';

    if (revisionValue === 0) {
      revisionId = null;
      apiEndpoint = `/quotes/${quoteNumber}/revisions/null`;
    } else {
      const revisionIndex = revisionIds.length - (versionNumber - 1);
      revisionId = revisionIds[revisionIndex];

      if (!revisionId) {
        return;
      }
      apiEndpoint = `/quotes/${quoteNumber}/revisions/${revisionId}`;
    }

    try {
      const abortController = new AbortController();
      const revisionResponse = await secureGet(apiEndpoint, showSnackbar, {
        signal: abortController.signal
      });
      console.log("revisionResponse", revisionResponse);
      const storageKey = revisionId === null ? 'original' : revisionId;
      setRevisionDetails(prev => ({
        ...prev,
        [storageKey]: revisionResponse.data
      }));
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(`[VERSION SELECT] ❌ Failed to fetch ${revisionId === null ? 'original' : `revision ID ${revisionId}`}:`, error);
      }
    }
  };

  // ========== Event Handlers - Documents ==========
  const handleDocumentAddButtonClick = () => {
    setShowDocumentModule(true);
    setSelectedDocument(null);
  };

  const handleDocumentDelete = (document) => {
    setDocumentToDelete(document);
    setDeleteDocumentPopupOpen(true);
  };

  const handleDocumentDeleteConfirm = async () => {
    if (!documentToDelete) return;
    try {
      await secureDelete(`/reception-documents/${id}/attachments/${documentToDelete.id}`, showSnackbar);
      showSnackbar(t('confirmation.documentDelete'), 'success');
      setDeleteDocumentPopupOpen(false);
      setDocumentToDelete(null);
      setLoadedTabs(prev => ({ ...prev, documents: false }));
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const handleDocumentFilterChange = (filterName, filterValue) => {
    const updated = { ...documentFilters, [filterName]: filterValue };
    setDocumentFilters(updated);
    setLoadedTabs(prev => ({ ...prev, documents: false }));
  };

  const resetDocumentFilters = () => {
    const reset = { type: "" };
    setDocumentFilters(reset);
    setLoadedTabs(prev => ({ ...prev, documents: false }));
  };

  const handleDocumentDetails = (document) => {
    if (document.fileUrl) {
      window.open(document.fileUrl, '_blank');
    } else {
      console.error("No fileUrl available for document:", document);
    }
  };

  const handleDocumentDownload = (document) => {
    console.log("download", document);
  };

  // ========== Event Handlers - Module ==========
  const handleModuleClose = (action) => {
    if (action === "submit") {
      if (activeTab === 'quotes') {
        setLoadedTabs(prev => ({ ...prev, quotes: false }));
        setQuoteNotFound(false);
        fetchfr();
        fetchCreditNotes();
      } else if (activeTab === 'bri') {
        setLoadedTabs(prev => ({ ...prev, bri: false }));
        fetchfr();
      } else if (activeTab === 'documents') {
        setLoadedTabs(prev => ({ ...prev, documents: false }));
        fetchfr();
      }
    }
    setShowModule(false);
    setShowDocumentModule(false);
    setSelectedQuote(null);
    setSelectedDocument(null);
  };

  // ========== Computed Values ==========
  const headerProps = {
    entity: "fr",
    entityId: fr?.receptionNumber,
    status: t(`fr_status.${fr?.status}`),
    rawStatus: fr?.status,
    client: { code: fr?.clientCode, name: fr?.clientName, to: `/clients/details/${fr?.clientId}` },
    complementary: { id: fr?.projectNumber, to: `/projects/details/${fr?.projectId}` },
    createdAt: fr?.createdAt,
    fuelPercent: fr?.fuelLevel,
    mileage: fr?.kilometers,
    vehicle: vehicle ? {
      brand: vehicle?.brand?.name || '—',
      model: vehicle?.model || '—',
      type: vehicle?.type || '—',
      color: vehicle?.color || '—',
      plate: vehicle?.immatriculationNumber || '—',
      vin: vehicle?.vinNumber || '—',
    } : null,
  };

  const headerActions = (
    <div className="flex items-center gap-3">
      {(role === "ROLE_SERVICE_CONSULTANT" || role === "ROLE_ADMIN") && (!quote || quoteNotFound) && (
        <PrimaryButton
          label={t(`primaryButtons.add_quote`)}
          icon={AddIcon}
          onClick={handleAddClick}
        />
      )}
      {(role === "ROLE_SERVICE_CONSULTANT" || role === "ROLE_ADMIN") && fr?.status !== "SIGNED" && fr?.status !== "CREATED" && fr?.projectNumber === null && (
        <button
          onClick={handleMarkAsSigned}
          disabled={signing}
          className="flex items-center justify-center px-4 py-3 h-[40px] bg-[#06D6A0] shadow-[0px_6px_26px_rgba(98,107,125,0.07)] rounded-[12px] text-white text-[14px] font-bold space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {signing ? (
            <>
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Chargement...</span>
            </>
          ) : (
            <>
              <img src={GenerateIcon} alt="Sign Fiche Reception" className="w-6 h-6" />
              <span>Signer la fiche</span>
            </>
          )}
        </button>
      )}
    </div>
  );

  // ========== useEffect Hooks ==========
  useEffect(() => {
    fetchfr();
  }, [fetchfr]);

  useEffect(() => {
    if (activeTab === 'quotes') {
      fetchQuote();
    } else if (activeTab === 'bri') {
      fetchCreditNotes();
    } else if (activeTab === 'documents') {
      fetchDocuments();
    }
  }, [activeTab, fetchQuote, fetchCreditNotes, fetchDocuments]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const openDropdownIds = Object.keys(historyDropdownOpen).filter(
        id => historyDropdownOpen[id]
      );

      if (openDropdownIds.length === 0) return;

      const clickedOutside = openDropdownIds.every(quoteId => {
        const dropdownRef = dropdownRefs.current[quoteId];
        return dropdownRef && !dropdownRef.contains(event.target);
      });

      if (clickedOutside) {
        setHistoryDropdownOpen(prev => {
          const updated = { ...prev };
          openDropdownIds.forEach(id => {
            updated[id] = false;
          });
          return updated;
        });
      }
    };

    const hasOpenDropdown = Object.values(historyDropdownOpen).some(isOpen => isOpen);
    if (hasOpenDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [historyDropdownOpen]);

  // ========== Early Returns ==========
  if (!fr) {
    return null;
  }

  // ========== Render ==========
  const quotesCount = quote ? quote.revisionNumber : 0;
  const briCount = creditNotes?.length ?? 0;
  const documentsCount = documents?.length ?? 0;

  return (
    <div>
      <Helmet>
        <title>Drivnest - {t("titles.fiche_details")}</title>
      </Helmet>

      <div className="flex flex-col mb-[20px]">
        <div className="flex items-center justify-between">
          <div className="flex flex-col w-full">
            <Breadcrumbs />
            {loading || !fr.id || !vehicle ? (
              <LoadingLogo />
            ) : (
              <DetailsHeader {...headerProps} rightActions={headerActions} />
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      {!loading && fr.id && vehicle && (
        <div className="flex bg-gray-200 rounded-[12px] p-1 w-fit">
          <button
            className={`px-12 py-3 text-[14px] font-bold rounded-[12px] ${activeTab === 'quotes'
              ? 'bg-white text-[#043F88] shadow'
              : 'text-[#ACAFC6]'
              }`}
            onClick={() => setActiveTab('quotes')}
          >
            {`${t(`titles.quotes`).toUpperCase()}`}
          </button>
          <button
            className={`px-12 py-3 text-[14px] font-bold rounded-[12px] ${activeTab === 'bri'
              ? 'bg-white text-[#043F88] shadow'
              : 'text-[#ACAFC6]'
              }`}
            onClick={() => setActiveTab('bri')}
          >
            {`${t(`titles.bri`).toUpperCase()}`}
          </button>
          <button
            className={`px-12 py-3 text-[14px] font-bold rounded-[12px] ${activeTab === 'documents'
              ? 'bg-white text-[#043F88] shadow'
              : 'text-[#ACAFC6]'
              }`}
            onClick={() => setActiveTab('documents')}
          >
            {`${t(`titles.documents`).toUpperCase()}`}
          </button>
        </div>
      )}

      {/* Quotes Tab */}
      {!loading && fr.id && vehicle && activeTab === "quotes" && (
        <div className="pt-2">
          {tabLoading.quotes ? (
            <LoadingLogo />
          ) : quote && !quoteNotFound ? (
            <div>
              <div className="px-0 py-2">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="text-[24px] text-[#043F87] font-semibold">
                      ID {quote.quoteNumber}
                    </h3>


                    {quote && (revisionIds.length > 0 || quote.revisionNumber > 1) && (
                      <div
                        className="relative"
                        ref={(el) => {
                          if (el) {
                            dropdownRefs.current[quote.id] = el;
                          } else {
                            delete dropdownRefs.current[quote.id];
                          }
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => handleHistoryToggle(quote.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-white rounded-[12px] text-primary opacity-100 font-bold text-[12px] shadow-[0px_20px_80px_#00000011]"
                        >
                          <img src={HistoryIcon} alt="" className="w-4 h-4" />
                          <span>{t('primaryButtons.history')}</span>
                          <img src={ChevronDownIcon} alt="" className="w-4 h-4" />
                        </button>

                        {historyDropdownOpen[quote.id] && (
                          <div className="absolute right-0 mt-2 w-64 bg-white rounded-[12px] shadow-[0px_12px_26px_#0000001C] z-20 max-h-96 overflow-y-auto">
                            <button
                              key={1}
                              type="button"
                              onClick={() => handleVersionSelect(quote.id, 0)}
                              className={`w-full px-4 py-3 text-left text-[14px] border-b border-gray-100 ${getSelectedVersion(quote) === 1 ? 'bg-[#EEF3FB] font-bold text-primary' : 'text-primary opacity-100'
                                }`}
                            >
                              <div className="font-semibold">
                                Version 1 (Original)
                              </div>
                            </button>

                            {revisionIds.slice().reverse().map((revisionId, index) => {
                              const versionNumber = index + 2;
                              const revisionValue = versionNumber - 1;
                              const totalVersions = revisionIds.length + 1;
                              const isLatest = versionNumber === totalVersions;
                              const selectedVersion = getSelectedVersion(quote);
                              const isSelected = versionNumber === selectedVersion;
                              const revisionDetail = revisionDetails[revisionId];

                              return (
                                <button
                                  key={revisionId || versionNumber}
                                  type="button"
                                  onClick={() => handleVersionSelect(quote.id, revisionValue)}
                                  className={`w-full px-4 py-3 text-left text-[14px] border-b border-gray-100 last:border-b-0 ${isSelected ? 'bg-[#EEF3FB] font-bold text-primary' : 'text-primary opacity-100'
                                    }`}
                                >
                                  <div className="font-semibold">
                                    {`Version ${versionNumber}${isLatest ? ' (Dernière)' : ''}`}
                                  </div>
                                  {revisionDetail?.changes && revisionDetail.changes.length > 0 && (
                                    <div className="text-[12px] text-gray-600 mt-1">
                                      {revisionDetail.changes.length} modification{revisionDetail.changes.length > 1 ? 's' : ''}
                                    </div>
                                  )}
                                  {revisionDetail?.justification && (
                                    <div className="text-[12px] text-gray-500 mt-1 italic truncate">
                                      {revisionDetail.justification}
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                    {(role === "ROLE_SERVICE_CONSULTANT" || role === "ROLE_ADMIN") && (
                      <>
                        {fr?.projectStatus !== 'DELIVERED' && (
                          <button
                            onClick={() => handleEditClick(quote)}
                            className="flex items-center justify-center px-4 py-2 bg-white text-primary opacity-100 rounded-[12px] font-bold text-[12px] shadow-[0px_20px_80px_#00000011]"
                          >
                            <img src={EditIcon} alt="" className="w-4 h-4 mr-2" />
                            {t('actions.modify')}
                          </button>
                        )}
                        {quote?.status !== "APPROVED" && (
                          <button
                            onClick={() => handleDeleteClick(quote)}
                            className="flex items-center justify-center px-4 py-2 bg-[#EF466F] text-white rounded-[12px] font-semibold text-[12px] shadow-[0px_20px_80px_#00000011]"
                          >
                            <img src={TrashIcon} alt="" className="w-4 h-4 mr-2" />
                            {t('actions.delete')}
                          </button>
                        )}
                      </>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-6 ml-auto">
                    <div className="flex items-center gap-2">
                      {(() => {
                        const selectedVersion = getSelectedVersion(quote);
                        const totalVersions = revisionIds.length + 1;
                        let advancePayment = null;

                        if (selectedVersion === totalVersions || selectedVersion === quote.revisionNumber) {
                          advancePayment = quote?.advancePayment;
                        } else if (selectedVersion === 1) {
                          const originalDetail = revisionDetails['original'];
                          advancePayment = originalDetail?.advancePayment || quote?.advancePayment;
                        } else {
                          const revisionIndex = revisionIds.length - (selectedVersion - 1);
                          const revisionId = revisionIds[revisionIndex];
                          if (revisionId && revisionDetails[revisionId]) {
                            advancePayment = revisionDetails[revisionId].advancePayment || quote?.advancePayment;
                          } else {
                            advancePayment = quote?.advancePayment;
                          }
                        }

                        return advancePayment ? (
                          <div className="flex items-center gap-2">
                            <span className="text-[20px] text-[#3771B0] font-semibold">ACOMPTE:</span>
                            <span className="text-[20px] text-[#38485C] font-bold">
                              {formatAmount(advancePayment)}
                            </span>
                          </div>
                        ) : null;
                      })()}
                      <span className="text-[20px] text-[#3771B0] font-semibold">TOTAL HT:</span>
                      <span className="text-[20px] text-[#38485C] font-bold">
                        {(() => {
                          const selectedVersion = getSelectedVersion(quote);
                          const totalVersions = revisionIds.length + 1;

                          if (selectedVersion === totalVersions || selectedVersion === quote.revisionNumber) {
                            return formatAmount(quote?.priceHT);
                          }

                          if (selectedVersion === 1) {
                            const originalDetail = revisionDetails['original'];
                            if (originalDetail) {
                              return formatAmount(originalDetail.priceHT || 0);
                            }
                            return formatAmount(quote?.priceHT);
                          }

                          const revisionIndex = revisionIds.length - (selectedVersion - 1);
                          const revisionId = revisionIds[revisionIndex];
                          if (revisionId && revisionDetails[revisionId]) {
                            return formatAmount(revisionDetails[revisionId].priceHT || 0);
                          }
                          return formatAmount(quote?.priceHT);
                        })()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[20px] text-[#3771B0] font-semibold">TOTAL TTC:</span>
                      <span className="text-[20px] text-[#38485C] font-bold">
                        {(() => {
                          const selectedVersion = getSelectedVersion(quote);
                          const totalVersions = revisionIds.length + 1;

                          if (selectedVersion === totalVersions || selectedVersion === quote.revisionNumber) {
                            return formatAmount(quote?.priceTTC);
                          }

                          if (selectedVersion === 1) {
                            const originalDetail = revisionDetails['original'];
                            if (originalDetail) {
                              return formatAmount(originalDetail.priceTTC || 0);
                            }
                            return formatAmount(quote?.priceTTC);
                          }

                          const revisionIndex = revisionIds.length - (selectedVersion - 1);
                          const revisionId = revisionIds[revisionIndex];
                          if (revisionId && revisionDetails[revisionId]) {
                            return formatAmount(revisionDetails[revisionId].priceTTC || 0);
                          }
                          return formatAmount(quote?.priceTTC);
                        })()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Display Selected Version */}
              {(() => {
                const selectedVersion = getSelectedVersion(quote);
                const totalVersions = revisionIds.length + 1;

                if (selectedVersion === totalVersions || selectedVersion === quote.revisionNumber) {
                  return (
                    <div className="">
                      <div className="space-y-2">
                        {quote?.details?.map((detail, index) => (
                          <ProductRow key={index} product={detail} variant="quote" />
                        ))}
                      </div>
                    </div>
                  );
                }

                if (selectedVersion === 1) {
                  const originalDetail = revisionDetails['original'];

                  if (originalDetail) {
                    return (
                      <div className="">
                        <div className="space-y-2">
                          {originalDetail.details?.map((detail, index) => (
                            <ProductRow key={index} product={detail} variant="quote" />
                          ))}
                        </div>
                      </div>
                    );
                  } else {
                    return (
                      <div className="flex justify-center items-center py-8">
                        <LoadingLogo />
                      </div>
                    );
                  }
                }

                const revisionIndex = revisionIds.length - (selectedVersion - 1);
                const revisionId = revisionIds[revisionIndex];

                if (!revisionId) {
                  return null;
                }

                if (revisionDetails[revisionId]) {
                  const revisionDetail = revisionDetails[revisionId];

                  return (
                    <div className="">
                      <div className="space-y-2">
                        {revisionDetail.details?.map((detail, index) => (
                          <ProductRow key={index} product={detail} variant="quote" />
                        ))}
                      </div>

                      {revisionDetail.changes && revisionDetail.changes.length > 0 && (
                        <div className="mt-6 bg-gray-50 rounded-[12px] p-4">
                          <h5 className="text-[16px] font-semibold text-[#707070] mb-3">
                            Modifications de cette version ({revisionDetail.changes.length})
                          </h5>
                          <div className="space-y-2">
                            {revisionDetail.changes.map((change, changeIndex) => (
                              <div
                                key={changeIndex}
                                className="bg-white rounded-[8px] p-3 text-[14px]"
                              >
                                <div className="flex items-start gap-2">
                                  <span className="font-semibold text-[#043F88]">
                                    {change.changeType === 'ITEM_ADDITION' && '➕ Ajout'}
                                    {change.changeType === 'ITEM_REMOVAL' && '➖ Suppression'}
                                    {change.changeType === 'QUANTITY_CHANGE' && '📊 Modification quantité'}
                                    {!change.changeType && '❓ Type inconnu'}
                                  </span>
                                  <span className="text-[#38485C]">
                                    {change.productName && `- ${change.productName}`}
                                  </span>
                                </div>
                                {change.changeType === 'QUANTITY_CHANGE' && (
                                  <div className="mt-1 text-[12px] text-gray-600">
                                    Quantité: {change.oldQuantity} → {change.newQuantity}
                                  </div>
                                )}
                                {change.changeType === 'ITEM_REMOVAL' && change.oldQuantity && (
                                  <div className="mt-1 text-[12px] text-gray-600">
                                    Quantité supprimée: {change.oldQuantity}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                } else {
                  return (
                    <div className="flex justify-center items-center py-8">
                      <LoadingLogo />
                    </div>
                  );
                }
              })()}
            </div>
          ) : null}
        </div>
      )}

      {/* Credit Notes Tab */}
      {!loading && fr.id && vehicle && activeTab === 'bri' && (
        <div className="pt-2">
          {tabLoading.bri ? (
            <LoadingLogo />
          ) : creditNotes && creditNotes.length > 0 ? (
            creditNotes.map((creditNote) => (
              <div key={creditNote.id} className="mb-8">
                <div className="px-0 py-2">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="text-[24px] text-[#043F87] font-semibold">
                        {creditNote.creditNoteNumber || creditNote.id}
                      </h3>
                    </div>

                    <div className="flex flex-wrap items-center gap-6 ml-auto">
                      <div className="flex items-center gap-2">
                        <span className="text-[20px] text-[#3771B0] font-semibold">TOTAL HT:</span>
                        <span className="text-[20px] text-[#38485C] font-bold">
                          {formatAmount(creditNote.priceHT || 0)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[20px] text-[#3771B0] font-semibold">TOTAL TTC:</span>
                        <span className="text-[20px] text-[#38485C] font-bold">
                          {formatAmount(creditNote.priceTTC || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  {creditNote.details?.map((detail, index) => (
                    <ProductRow key={index} product={detail} variant="quote" />
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12 text-gray-500">
            </div>
          )}
        </div>
      )}

      {/* Documents Tab */}
      {!loading && fr.id && vehicle && activeTab === 'documents' && (
        <div>

          <div className="flex items-end gap-3 mb-3">
            <div className="flex-1">
              <Filter
                filters={[
                  {
                    name: "type",
                    type: "dropdown",
                    label: "Type de document",
                    options: [
                      { value: "VEHICLE_PICTURE", label: t("document_types.VEHICLE_PICTURE") },
                      { value: "INSURANCE_DOCUMENT", label: t("document_types.INSURANCE_DOCUMENT") },
                      { value: "IDENTIFICATION_DOCUMENT", label: t("document_types.IDENTIFICATION_DOCUMENT") },
                      { value: "SIGNED_RECEPTION_DOCUMENT", label: t("document_types.SIGNED_RECEPTION_DOCUMENT") },
                      { value: "OTHER", label: t("document_types.OTHER") },
                    ],
                  }
                ]}
                onFilterChange={handleDocumentFilterChange}
                resetFilters={resetDocumentFilters}
                filterValues={documentFilters}
              />
            </div>

            <button
              type="button"
              onClick={handleDocumentAddButtonClick}
              className="bg-[#16283E] w-[48px] h-[48px] rounded-[12px] flex items-center justify-center"
            >
              <img src={plusIcon} alt="Add" className="w-6 h-6" />
            </button>
          </div>
          {tabLoading.documents ? (
            <LoadingLogo />
          ) : (
            <>

              {documents.length > 0 ? (
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3">
                  {documents.map((document, index) => (
                    <GenericCard
                      key={index}
                      title={t(`document_types.${document.type}`) || ''}
                      subtitle={document.originalFileName || ''}
                      optionsMenu={
                        <div onClick={(e) => e.stopPropagation()}>
                          <OptionsMenu options={[
                            { label: t('actions.details'), icon: EyeIcon, action: () => handleDocumentDetails(document) },
                            { label: t('actions.download'), icon: DownloadIcon, action: () => handleDocumentDownload(document) },
                            { label: t('actions.delete'), icon: DeleteIcon, color: '#EF466F', action: () => handleDocumentDelete(document) },
                          ]} />
                        </div>
                      }
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Modals */}
      {showModule && (
        <ModuleWrapper
          moduleType="quotes"
          mode={selectedQuote ? "modify" : "add"}
          onClose={handleModuleClose}
          onSuccess={(msg) => showSnackbar(msg, 'success')}
          initialValues={selectedQuote ?
            { ...selectedQuote, receptionDocumentId: id, frStatus: fr?.status, clientName: fr?.clientName, clientId: fr?.clientId, project: fr?.projectNumber } : { receptionDocumentId: id, frStatus: fr?.status, clientName: fr?.clientName, clientId: fr?.clientId, project: fr?.projectNumber }}
        />
      )}

      {showDocumentModule && (
        <ModuleWrapper
          moduleType="documents"
          mode={"add"}
          onClose={handleModuleClose}
          onSuccess={(msg) => showSnackbar(msg, 'success')}
          initialValues={{ receptionDocumentId: id }}
        />
      )}

      {deletePopupOpen && (
        <DeletePopup
          open={deletePopupOpen}
          onCancel={() => {
            setDeletePopupOpen(false);
            setQuoteToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {deleteDocumentPopupOpen && (
        <DeletePopup
          open={deleteDocumentPopupOpen}
          onCancel={() => {
            setDeleteDocumentPopupOpen(false);
            setDocumentToDelete(null);
          }}
          onConfirm={handleDocumentDeleteConfirm}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default FicheReceptionDetails;
